import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useMessengerLogin, usePatientChatRoom } from "@/hooks/usePatientMessenger";
import { ChatConfiguration, ChatGraphQLError } from "@/utils/GraphQLClient/messenger";
import { isMessengerEnabled } from "@/utils/feature-flags";

import type { IConfig } from "@ethora/chat-component/dist/types/types";

interface MessengerContextValue {
  enabled: boolean;
  fhirPatientId: string | null;
  isLoading: boolean;
  token: string | null;
  chatRoomJid: string | null;
  config: ChatConfiguration | null;
  error?: ChatGraphQLError;
  setFhirPatientId: (id: string | null) => void;
  setChatRoomJid: (patientId: string) => void;
  removeChatRoomJid: () => void;
  xmppProviderFlags: IConfig;
}

const MessengerContext = createContext<MessengerContextValue>({
  fhirPatientId: null,
  isLoading: true,
  token: null,
  chatRoomJid: null,
  config: null,
  error: undefined,
  setFhirPatientId: () => {},
  setChatRoomJid: () => {},
  removeChatRoomJid: () => {},
  xmppProviderFlags: {},
  enabled: true,
});

export const MessengerProvider = ({ children }: { children: React.ReactNode }) => {
  const { token, config, loading, error: loginError } = useMessengerLogin();
  const [fhirPatientId, setFhirPatientId] = useState<string | null>(null);
  const [chatRoomJid, setChatRoomJidState] = useState<string | null>(null);

  const {
    room_jid,
    error: chatRoomError,
    loading: chatRoomLoading,
    reexecute_query,
  } = usePatientChatRoom(fhirPatientId ?? undefined);

  useEffect(() => {
    if (fhirPatientId) {
      setChatRoomJidState(room_jid ?? null);
    } else {
      setChatRoomJidState(null);
    }
  }, [fhirPatientId, room_jid]);

  const setChatRoomJid = useCallback(() => {
    reexecute_query?.();
  }, [reexecute_query]);

  const removeChatRoomJid = useCallback(() => {
    setChatRoomJidState(null);
  }, []);

  const xmppProviderFlags: IConfig = useMemo(
    () => ({
      // UI flags — must match Chat's config so the store always has them
      disableInteractions: true,
      setRoomJidInPath: false,
      disableNewChatButton: true,
      disableRoomConfig: true,
      disableProfilesInteractions: true,
      disableChatInfo: {
        disableHeader: false,
        disableDescription: true,
        disableType: true,
        disableMembers: true,
        disableChatHeaderMenu: true,
      },
      chatHeaderSettings: {
        hide: false,
        disableCreate: true,
        disableMenu: true,
      },
      translates: { enabled: true }, // Must match Chat's config (see above)
    }),
    []
  );

  const value = useMemo(
    () => ({
      fhirPatientId,
      isLoading: loading || chatRoomLoading || (!chatRoomJid && !(loginError || chatRoomError)),
      token: token ?? null,
      config: config ?? null,
      chatRoomJid,
      error: loginError || chatRoomError,
      setFhirPatientId,
      setChatRoomJid,
      removeChatRoomJid,
      xmppProviderFlags,
      enabled: isMessengerEnabled(),
    }),
    [
      chatRoomError,
      chatRoomJid,
      chatRoomLoading,
      config,
      fhirPatientId,
      loading,
      loginError,
      removeChatRoomJid,
      setChatRoomJid,
      token,
      xmppProviderFlags,
    ]
  );

  return <MessengerContext.Provider value={value}>{children}</MessengerContext.Provider>;
};

export const useMessengerContext = (): MessengerContextValue => useContext(MessengerContext);
